import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  applyUrlQueryPatches,
  parseUrlQuery,
  serializeUrlQuery,
  type UrlQueryPatch,
  type UrlQuerySchema,
} from 'utils/urlQueryState'

export type SetUrlQueryState<S extends Record<string, unknown>> = (patch: UrlQueryPatch<S>) => void

type UrlWriteJob = {
  apply: (params: URLSearchParams) => URLSearchParams
  setSearchParams: (next: URLSearchParams, opts: { replace: boolean }) => void
}

/**
 * Cross-hook write queue. Facility (and similar pages) mount two useUrlQueryState
 * instances on the same search string; react-router@6.3 setSearchParams is not a
 * functional updater, so last write with a stale snapshot would wipe sibling keys
 * (e.g. vendor effect erasing tab=fleet). One microtask applies every pending
 * patch onto the latest location.search, then navigates once.
 *
 * Do not wrap the navigate in startTransition: live pages (robot telemetry,
 * station polling) keep rendering at normal priority and can starve a
 * transition, so the address bar updates while the tab UI stays stale until F5.
 */
const pendingUrlWrites: UrlWriteJob[] = []
let urlWriteFlushScheduled = false

function scheduleUrlWrite(job: UrlWriteJob) {
  pendingUrlWrites.push(job)
  if (urlWriteFlushScheduled) return
  urlWriteFlushScheduled = true
  queueMicrotask(() => {
    urlWriteFlushScheduled = false
    const jobs = pendingUrlWrites.splice(0)
    if (!jobs.length) return

    // Jobs queued while this flush was already running still belong in this write.
    const lateJobs = pendingUrlWrites.splice(0)
    const allJobs = lateJobs.length ? [...jobs, ...lateJobs] : jobs

    let params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '')
    const before = params.toString()
    for (const nextJob of allJobs) {
      params = nextJob.apply(params)
    }
    if (params.toString() === before) {
      return
    }
    // Any hook's setSearchParams shares the same router navigate.
    allJobs[allJobs.length - 1].setSearchParams(params, { replace: true })
  })
}

/**
 * Bidirectional UI ↔ URL search-param state.
 * Writes use history.replace so filter/tab clicks do not fill the back stack.
 * Defaults are omitted from the URL (see field.serialize).
 *
 * Multiple setUrl calls in the same synchronous turn (e.g. Clear Filters, or
 * setCustomStartDate + setDateRangeType) are coalesced into one URL write so a
 * stale searchParams snapshot cannot wipe a sibling patch.
 * Patches from *different* useUrlQueryState hooks on the same page are coalesced
 * the same way (see scheduleUrlWrite).
 *
 * setUrl also applies an optimistic snapshot immediately so tab clicks paint
 * before the router searchParams subscription catches up.
 */
export function useUrlQueryState<S extends Record<string, unknown>>(
  schema: UrlQuerySchema<S>,
  options?: { dropKeys?: string[] }
): [S, SetUrlQueryState<S>] {
  const [searchParams, setSearchParams] = useSearchParams()
  const schemaRef = useRef(schema)
  schemaRef.current = schema
  const dropKeysRef = useRef(options?.dropKeys)
  dropKeysRef.current = options?.dropKeys
  const pendingPatchesRef = useRef<Array<UrlQueryPatch<S>>>([])
  const flushScheduledRef = useRef(false)
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams
  const [optimistic, setOptimistic] = useState<S | null>(null)

  const urlState = parseUrlQuery(searchParams, schema)
  const state = optimistic ?? urlState
  const searchKey = searchParams.toString()

  useEffect(() => {
    setOptimistic(prev => {
      if (prev == null) return null
      // Keep the click snapshot while our write is still in the microtask/transition.
      if (pendingPatchesRef.current.length > 0 || flushScheduledRef.current) return prev
      return null
    })
  }, [searchKey])

  const flushPending = useCallback(() => {
    flushScheduledRef.current = false
    const patches = pendingPatchesRef.current.splice(0)
    if (!patches.length) return

    scheduleUrlWrite({
      setSearchParams,
      apply: prevParams => {
        // Patches queued after this job was enqueued but before the global flush.
        const latePatches = pendingPatchesRef.current.splice(0)
        const allPatches = latePatches.length ? [...patches, ...latePatches] : patches
        const current = parseUrlQuery(prevParams, schemaRef.current)
        const nextState = applyUrlQueryPatches(current, allPatches)
        return serializeUrlQuery(prevParams, schemaRef.current, nextState, dropKeysRef.current)
      },
    })
  }, [setSearchParams])

  const setState = useCallback<SetUrlQueryState<S>>(
    patch => {
      pendingPatchesRef.current.push(patch)
      setOptimistic(prev => {
        const current = prev ?? parseUrlQuery(searchParamsRef.current, schemaRef.current)
        return applyUrlQueryPatches(current, [patch])
      })
      if (flushScheduledRef.current) return
      flushScheduledRef.current = true
      // Microtask: coalesce every setUrl from the same click/handler before writing.
      queueMicrotask(flushPending)
    },
    [flushPending]
  )

  return [state, setState]
}
